import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Observable, throwError, timer } from 'rxjs';
import { catchError, retry } from 'rxjs/operators';
import { ToastService } from '../services/toast.service';
import { LoggerService } from '../services/logger.service';
import { RETRY_ON_FAILURE, SILENCE_ERRORS, isCapabilityUnavailable, toApiError } from '../http/api-error';

/**
 * Turns HTTP failures into something the user and a support engineer can both act on.
 *
 * The audit's finding was that failure is invisible here: across 38 components and
 * ~60 services exactly ONE call site surfaces an error to the user. Everything else
 * either swallows the failure into a permanently-spinning skeleton or logs it to a
 * console nobody has open. That is why "Estimated Avails keeps loading" was reported
 * as a mystery rather than as the 400 it actually was — the information existed, it
 * just never reached a human.
 *
 * Three jobs:
 *
 *   1. **Correlate.** Every request carries an `X-Request-Id`. When a user reports a
 *      failure, that id appears in both the toast and the BFF's log line, which turns
 *      "it broke yesterday" into one greppable string.
 *   2. **Retry, narrowly.** Opt-in only, and only for transport-level and 5xx faults.
 *      NOT enabled by default: `POST /chat` enqueues an agent job and `POST
 *      /avails/quote` bills an upstream quota, so a blind retry-on-5xx would
 *      double-charge both. Backoff is jittered — without jitter, a BFF restart makes
 *      every open tab retry in lockstep and re-create the stampede that caused it.
 *   3. **Report.** One toast, in one voice, unless the caller silences it.
 *
 * 401 is deliberately NOT auto-redirected to a login page. Session expiry mid-plan
 * would discard unsaved work with no warning, and the app's own auth guard already
 * owns that decision — an interceptor is the wrong layer to navigate from.
 */
/**
 * True once the page has begun unloading (reload, navigation away, tab close).
 *
 * A reload ABORTS every in-flight XHR, and the browser reports an abort to
 * Angular exactly as it reports a dead network: `HttpErrorResponse` with
 * `status === 0`. `RETRYABLE` contains 0 — correctly, for a real transport blip
 * — so a plain reload was surfacing the user's own action as
 * "Taking longer than usual / the server is busy". QA reported it as an
 * intermittent popup on refresh; nginx logged the same requests as 499
 * (client closed the connection) with nothing failing server-side.
 *
 * A module-level flag rather than interceptor state: the listeners must be
 * registered once for the app, not once per intercepted request.
 */
let pageUnloading = false;
if (typeof window !== 'undefined') {
  const markUnloading = () => { pageUnloading = true; };
  // `pagehide` fires for the bfcache path where `beforeunload` does not; both
  // are registered because either can be the one that runs first.
  window.addEventListener('pagehide', markUnloading);
  window.addEventListener('beforeunload', markUnloading);
}

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  /** Transport failure (0) or a server-side fault. 4xx means "don't ask again". */
  private static readonly RETRYABLE = new Set([0, 502, 503, 504]);

  private static readonly MAX_RETRIES = 2;
  private static readonly BASE_DELAY_MS = 400;

  constructor(
    private readonly toast: ToastService,
    private readonly logger: LoggerService,
  ) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const requestId = ErrorInterceptor.newRequestId();
    const tagged = req.clone({ setHeaders: { 'X-Request-Id': requestId } });

    const silenced = req.context.get(SILENCE_ERRORS);
    // Idempotent reads auto-retry: a GET/HEAD has no side effect, so a transient
    // transport blip (status 0) or 5xx under load should be retried transparently
    // rather than shown to the user as a hard error. Mutations still opt in
    // explicitly (RETRY_ON_FAILURE) because retrying a POST can double-charge.
    const idempotent = req.method === 'GET' || req.method === 'HEAD';
    const retryable = idempotent || req.context.get(RETRY_ON_FAILURE);

    return next.handle(tagged).pipe(
      retry({
        count: retryable ? ErrorInterceptor.MAX_RETRIES : 0,
        delay: (error: unknown, attempt: number) => {
          if (
            !(error instanceof HttpErrorResponse) ||
            !ErrorInterceptor.RETRYABLE.has(error.status)
          ) {
            return throwError(() => error);
          }
          // Full jitter: uniform over [0, base * 2^attempt). Retrying on a fixed
          // schedule synchronises every client that failed at the same moment.
          const ceiling = ErrorInterceptor.BASE_DELAY_MS * 2 ** attempt;
          return timer(Math.random() * ceiling);
        },
      }),
      catchError((error: HttpErrorResponse) => {
        const apiError = toApiError(error, requestId);

        // A platform that has no implementation of a step yet is not a failure: the
        // caller shows its Coming-soon state (TikTok plans spec §4.4). Computed here,
        // ahead of the log line below, so that an expected 409 logs at `info` instead
        // of `error` — nobody needs to be paged for a plan opening Coming soon.
        const capabilityOff = isCapabilityUnavailable(error);

        // URL and status only. An error body can carry the plan payload, and this
        // is the one log line that always runs, including in production.
        const logLine = `[http] ${req.method} ${req.url} → ${apiError.status} (req ${requestId})`;
        if (capabilityOff) {
          this.logger.info(logLine);
        } else {
          this.logger.error(logLine);
        }

        // STATUS 0 IS AN ABORT FAR MORE OFTEN THAN IT IS AN OUTAGE, and the two
        // are indistinguishable to Angular: a cancelled XHR and a dead network
        // both arrive as `HttpErrorResponse` with `status === 0`.
        //
        // This app cancels requests constantly and legitimately — a reload or
        // navigation aborts everything in flight, and RxJS tears down an
        // in-flight GET whenever its subscription ends (switching tactic tabs,
        // moving between plans). nginx logs each one as 499, "client closed
        // request", with nothing failing server-side. Every one of those was
        // being reported to the user as "the server is busy".
        //
        // So a status 0 is only worth a toast when the browser itself says the
        // machine is offline. `navigator.onLine === false` is reliable in the
        // negative direction (it can be wrongly true on a captive portal, never
        // wrongly false), which is the direction that matters here. Real server
        // faults — 502/503/504 — are untouched and still toast.
        const cancelled =
          apiError.status === 0 && (pageUnloading || navigator.onLine !== false);
        if (!silenced && !cancelled && !capabilityOff) {
          // A transient read that already exhausted its retries gets softer copy
          // — "the server is busy, we'll keep trying" reads very differently from
          // "cannot reach the server", and under load the former is the truth.
          // Writes keep "Couldn't save"; hard read failures keep "Couldn't load".
          const writing = !idempotent;
          if (writing) {
            this.toast.showError(apiError.message, "Couldn't save");
          } else if (apiError.offline || ErrorInterceptor.RETRYABLE.has(apiError.status)) {
            this.toast.showError(
              'The server is busy — still trying. This can happen under heavy load.',
              'Taking longer than usual');
          } else {
            this.toast.showError(apiError.message, "Couldn't load");
          }
        }

        return throwError(() => apiError);
      }),
    );
  }

  /** `crypto.randomUUID` needs a secure context; plain http dev origins do not have it. */
  private static newRequestId(): string {
    const uuid = globalThis.crypto?.randomUUID?.();
    if (uuid) return uuid;
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }
}
